'use client'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { captureApiError } from '@/utils/sentry-helpers'
import { useAuthenticate } from '@account-kit/react'
import { useRef, useState } from 'react'
import { useSiweAuth } from './hooks'
import { EmailStep, OtpStep } from './steps'
import type { AuthStep } from './types'
import { isRateLimitError, parseOtpError } from './utils'

type AuthDialogProps = {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function AuthDialog({ open, onOpenChange }: AuthDialogProps) {
	const [step, setStep] = useState<AuthStep>('email')
	const [email, setEmail] = useState('')
	const [otpCode, setOtpCode] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [isGoogleLoading, setIsGoogleLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const hasErrorRef = useRef(false)
	const { authenticate } = useAuthenticate()

	// SIWE wallet auth
	const {
		connect: connectWallet,
		isPending: isWalletPending,
		step: walletStep,
		error: walletError,
		clearError: clearWalletError,
	} = useSiweAuth()

	const resetState = () => {
		setStep('email')
		setEmail('')
		setOtpCode('')
		setError(null)
		setIsLoading(false)
		setIsGoogleLoading(false)
		hasErrorRef.current = false
		clearWalletError()
	}

	const handleSendCode = () => {
		if (!email) {
			return
		}
		setError(null)
		clearWalletError()
		setIsLoading(true)
		hasErrorRef.current = false

		authenticate(
			{ type: 'email', emailMode: 'otp', email },
			{
				onSuccess: () => {
					setIsLoading(false)
					onOpenChange(false)
					resetState()
				},
				onError: err => {
					hasErrorRef.current = true
					setIsLoading(false)
					const errorMessage = err.message || String(err)
					if (isRateLimitError(errorMessage)) {
						setError('Too many attempts. Please wait a moment and try again.')
					} else {
						setError(errorMessage || 'Failed to send verification code')
					}
				},
			}
		)

		setTimeout(() => {
			if (!hasErrorRef.current) {
				setIsLoading(false)
				setStep('otp')
			}
		}, 1000)
	}

	const handleVerifyCode = () => {
		if (!otpCode || otpCode.length !== 6) {
			return
		}
		setError(null)
		setIsLoading(true)

		authenticate(
			{ type: 'otp', otpCode },
			{
				onSuccess: () => {
					setIsLoading(false)
					onOpenChange(false)
					resetState()
				},
				onError: err => {
					setIsLoading(false)
					setError(parseOtpError(err.message || String(err)))
				},
			}
		)
	}

	const handleAutoSubmitOtp = (code: string) => {
		setIsLoading(true)
		authenticate(
			{ type: 'otp', otpCode: code },
			{
				onSuccess: () => {
					setIsLoading(false)
					onOpenChange(false)
					resetState()
				},
				onError: err => {
					setIsLoading(false)
					setError(parseOtpError(err.message || String(err)))
				},
			}
		)
	}

	const handleBack = () => {
		if (step === 'otp') {
			setStep('email')
			setOtpCode('')
		}
		setError(null)
	}

	const handleResendCode = () => {
		setOtpCode('')
		setError(null)
		handleSendCode()
	}

	const handleClose = (newOpen: boolean) => {
		if (!newOpen) {
			resetState()
		}
		onOpenChange(newOpen)
	}

	const handleGoogleLogin = () => {
		setError(null)
		clearWalletError()
		setIsGoogleLoading(true)

		authenticate(
			{
				type: 'oauth',
				authProviderId: 'google',
				mode: 'popup',
			},
			{
				onSuccess: () => {
					setIsGoogleLoading(false)
					onOpenChange(false)
					resetState()
				},
				onError: err => {
					setIsGoogleLoading(false)
					const errorMessage = err.message || String(err)
					setError(errorMessage || 'Failed to sign in with Google')
					captureApiError(errorMessage, {
						endpoint: 'google_auth',
					})
				},
			}
		)
	}

	const handleWalletConnect = async () => {
		setError(null)
		clearWalletError()
		await connectWallet()
		// If successful, useSiweAuth calls authenticate internally
		// which will trigger onSuccess and close the dialog via AlchemyAccountProvider
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				closeButtonClasses="size-[24px] text-brand-gray-secondary"
				closeButtonContainerClasses="top-[30px] cursor-pointer"
				className="gap-[32px] rounded-3xl bg-brand-dark-light p-[24px] sm:max-w-[420px]"
			>
				<DialogTitle className="sr-only">Sign in</DialogTitle>
				{step === 'email' && (
					<EmailStep
						email={email}
						setEmail={setEmail}
						onSubmit={handleSendCode}
						isLoading={isLoading}
						error={error}
						onGoogleLogin={handleGoogleLogin}
						isGoogleLoading={isGoogleLoading}
						onWalletConnect={handleWalletConnect}
						isWalletPending={isWalletPending}
						walletStep={walletStep}
						walletError={walletError}
					/>
				)}
				{step === 'otp' && (
					<OtpStep
						email={email}
						otpCode={otpCode}
						setOtpCode={setOtpCode}
						onSubmit={handleVerifyCode}
						onBack={handleBack}
						onResend={handleResendCode}
						onAutoSubmit={handleAutoSubmitOtp}
						isLoading={isLoading}
						error={error}
					/>
				)}
			</DialogContent>
		</Dialog>
	)
}
